// Variant picker: server-render variant switching + hover/idle prefetch warming (SNAPTRAP).
import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /**
   * Prefetched section responses, keyed by the `option_values` string of the request. Populated
   * on hover/focus and on idle after load, then drained by {@link fetchUpdatedSection} so a real
   * selection renders from memory with no network wait. See {@link #warmVariant}.
   * @type {Map<string, Promise<string>>}
   */
  #prefetchCache = new Map();

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /**
   * Warms the section + hero image for whichever option value is being hovered or focused, before
   * it's clicked. Arrow field so it keeps `this` when used directly as an event listener.
   * @param {Event} event
   */
  #handlePrefetchHover = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const input = target.closest('label')?.querySelector('input[type="radio"]');
    // Skip non-options and the already-selected value (its combination is already on screen).
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    this.#warmVariant(this.#optionValuesForHover(input));
  };

  connectedCallback() {
    super.connectedCallback();
    this.#captureFieldsetState();

    this.addEventListener('change', this.variantChanged.bind(this));

    // Speed: warm the section (and its hero image) for an option before it's actually clicked.
    // pointerover covers mouse/touch, focusin covers keyboard tabbing. On load, once the browser
    // is idle, pre-resolve every first-option value (e.g. every Shape) so even the first click —
    // and touch devices, which never fire a hover — render instantly from cache.
    if (this.#isMainProductPicker) {
      this.addEventListener('pointerover', this.#handlePrefetchHover);
      this.addEventListener('focusin', this.#handlePrefetchHover);
      this.#scheduleIdlePrefetch();
    }

    this.#resizeObserver.observe(this);
  }

  /**
   * Snapshots each fieldset's live radio inputs and its currently-checked index, used by the pill
   * animation and selection bookkeeping. Must be re-run after every morph: a shape change swaps the
   * later fieldsets (e.g. Size) for a different set of DOM nodes, so a snapshot taken only at
   * connect would hold stale/detached inputs and address the wrong elements.
   */
  #captureFieldsetState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      // Always push one entry per fieldset (empty when nothing is checked) so #checkedIndices stays
      // index-aligned with #radios / the fieldset list.
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });
  }

  /**
   * Whether this picker is the primary one on a product page (not a product card / quick-add
   * duplicate). Only the primary picker prefetches, to avoid redundant background fetches.
   * @returns {boolean}
   */
  get #isMainProductPicker() {
    return (
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');

      if (!Number.isNaN(fieldsetIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];
        // Derive the changed input's position from the live radio list rather than the Liquid
        // `data-input-index`, which is `forloop.index0` over ALL option values including those
        // hidden as unavailable (the position>1 `continue` in variant-main-picker.liquid). That
        // makes it non-contiguous with the rendered DOM, so it would address the wrong radio —
        // setting `data-current-checked` on the wrong label and breaking the pill animation.
        const inputIndex = radios ? radios.indexOf(target) : -1;

        if (radios && checkedIndices && fieldset && inputIndex !== -1) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      // If the change came from an earlier fieldset (e.g. Shape), drop any still-checked values
      // from later fieldsets (e.g. Size) rather than sending a combination that may no longer
      // exist. Sending a partial selection lets the server pick a sensible default for the
      // dropped option in the same request, instead of resolving an invalid combo that a
      // follow-up correction would have to overwrite a moment later (visible as a flash).
      const changedFieldsetIndex = Number.parseInt(selectedOption.dataset.fieldsetIndex ?? '', 10);
      const selectedValues = Number.isNaN(changedFieldsetIndex)
        ? this.selectedOptionsValues
        : this.getSelectedOptionsValuesUpTo(changedFieldsetIndex);
      params.push(`option_values=${selectedValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string, __resolvedDefaults?: boolean }} [options.detail] - Synchronous product select event detail. `__resolvedDefaults` is set internally when re-fetching with a server-defaulted option value already resolved, to avoid re-checking.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    // If this exact combination was already warmed (hover/focus/idle prefetch), render it straight
    // from memory instead of waiting on the network. The prefetch also pre-resolved the server's
    // default for any omitted option, so the two-step correction below is cached too.
    const prefetchKey = this.#optionValuesKeyFromUrl(requestUrl);
    const prefetched = prefetchKey ? this.#prefetchCache.get(prefetchKey) : undefined;
    const fetchLive = () => fetch(requestUrl, { signal: abortController.signal }).then((response) => response.text());
    // A warmed entry that failed resolves to '' — fall back to a live fetch so a click never no-ops.
    const responseTextPromise = prefetched ? prefetched.then((text) => text || fetchLive()) : fetchLive();

    responseTextPromise
      .then((responseText) => {
        // A newer selection superseded this one while a cached response was resolving — bail so we
        // don't render a stale combination over it. (Live fetches abort via the signal instead.)
        if (abortController.signal.aborted) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          // The server doesn't pick a default when a later fieldset (e.g. Size) is omitted from
          // the request — it comes back with that fieldset unchecked (Unavailable, no price).
          // Morphing that in would flash a broken intermediate state before the follow-up
          // #selectFirstValueInEmptyFieldsets() re-fetch corrects it. Instead, detect that case
          // on the raw response before ever touching the DOM, and silently re-fetch with the
          // full combo so only the final, correct state is ever rendered.
          const emptyFieldsetDefaults = detail.__resolvedDefaults ? [] : this.#getEmptyFieldsetDefaults(html);

          if (emptyFieldsetDefaults.length > 0) {
            const correctedUrl = new URL(requestUrl, window.location.origin);
            const existingValues = correctedUrl.searchParams.get('option_values');
            correctedUrl.searchParams.set(
              'option_values',
              [existingValues, ...emptyFieldsetDefaults].filter(Boolean).join(',')
            );

            this.fetchUpdatedSection(correctedUrl.pathname + correctedUrl.search, {
              morphElementSelector,
              detail: { ...detail, __resolvedDefaults: true },
            });
            return;
          }

          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }

          this.#selectFirstValueInEmptyFieldsets();
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    // Re-snapshot radios/checked state from the freshly morphed DOM before measuring the pill, so
    // it reflects the new fieldset contents (e.g. a shape's own set of sizes) rather than stale nodes.
    this.#captureFieldsetState();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * When an option's unavailable values are hidden (rather than disabled), changing an earlier
   * option can remove the radio that was previously checked in a later fieldset, leaving that
   * fieldset with no selection at all. Auto-select the first remaining value in any such
   * fieldset, which fires a native 'change' event and lets the normal flow resolve the variant.
   */
  #selectFirstValueInEmptyFieldsets() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    for (const fieldset of fieldsets) {
      if (!fieldset) continue;
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      if (radios.length === 0) continue;

      const hasChecked = Array.from(radios).some((radio) => radio instanceof HTMLInputElement && radio.checked);
      if (hasChecked) continue;

      const first = radios[0];
      if (first instanceof HTMLInputElement) {
        first.click();
      }
    }
  }

  /**
   * Inspects a raw fetched section response (before it's morphed into the page) for fieldsets
   * that came back with nothing checked — the server's response to a partial `option_values`
   * request when it can't infer a default for an omitted option. Returns the option value id of
   * the first radio in each such fieldset, so the caller can retry with a complete combination
   * instead of rendering the incomplete intermediate state.
   * @param {Document} newHtml - The raw fetched HTML, not yet morphed into the page.
   * @returns {string[]}
   */
  #getEmptyFieldsetDefaults(newHtml) {
    /** @type {string[]} */
    const defaults = [];
    const fieldsets = newHtml.querySelectorAll('variant-picker fieldset');

    for (const fieldset of fieldsets) {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      if (radios.length === 0) continue;

      const hasChecked = Array.from(radios).some((radio) => radio instanceof HTMLInputElement && radio.checked);
      if (hasChecked) continue;

      const first = radios[0];
      if (first instanceof HTMLInputElement && first.dataset.optionValueId) {
        defaults.push(first.dataset.optionValueId);
      }
    }

    return defaults;
  }

  /**
   * After load, once the browser is idle, pre-resolve every value of the first option (e.g. every
   * Shape) so the first interaction — including on touch devices that never hover — is instant.
   */
  #scheduleIdlePrefetch() {
    const run = () => {
      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
      const firstFieldset = fieldsets[0];
      if (!firstFieldset) return;

      const inputs = firstFieldset.querySelectorAll('input[type="radio"]');
      for (const input of inputs) {
        if (input instanceof HTMLInputElement && !input.checked) {
          // Low priority: speculative bulk warming must never compete with the user's own actions.
          this.#warmVariant(this.#optionValuesForHover(input), 'low');
        }
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 500);
    }
  }

  /**
   * The `option_values` a click on `input` would request: the currently-checked value of each
   * fieldset up to `input`'s own, with `input`'s value substituted at its index and later
   * fieldsets dropped (mirrors {@link getSelectedOptionsValuesUpTo} for a not-yet-checked input).
   * @param {HTMLInputElement} input
   * @returns {string[]}
   */
  #optionValuesForHover(input) {
    const changedIndex = Number.parseInt(input.dataset.fieldsetIndex ?? '', 10);
    if (Number.isNaN(changedIndex)) return [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    /** @type {string[]} */
    const values = [];

    fieldsets.forEach((fieldset, index) => {
      if (index > changedIndex || !fieldset) return;

      if (index === changedIndex) {
        if (input.dataset.optionValueId) values.push(input.dataset.optionValueId);
        return;
      }

      const checked = fieldset.querySelector('input:checked');
      const optionValueId = checked instanceof HTMLInputElement ? checked.dataset.optionValueId : undefined;
      if (optionValueId) values.push(optionValueId);
    });

    return values;
  }

  /**
   * Fetches and caches the section for a given option-value combination without touching the DOM,
   * so a later real selection of it renders from memory. Resolves the server's default for any
   * option the request omits (caching that corrected combo too) and preloads the resulting hero
   * gallery image.
   * @param {string[]} optionValueIds
   * @param {'auto' | 'low' | 'high'} [priority] - Fetch priority hint.
   */
  #warmVariant(optionValueIds, priority = 'auto') {
    if (!optionValueIds.length) return;

    const key = optionValueIds.join(',');
    if (this.#prefetchCache.has(key)) return;

    const promise = fetch(this.#buildPrefetchUrl(optionValueIds), { priority })
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const defaults = this.#getEmptyFieldsetDefaults(html);

        if (defaults.length > 0) {
          // The server left a later option unresolved; warm the corrected combo the real click
          // will actually end up requesting, and preload its image rather than this partial one's.
          this.#warmVariant([...optionValueIds, ...defaults], priority);
        } else {
          this.#warmGalleryImage(html);
        }

        return text;
      })
      .catch(() => {
        this.#prefetchCache.delete(key);
        return '';
      });

    this.#prefetchCache.set(key, promise);
  }

  /**
   * Builds a section-request URL for an explicit option-value combination (main product page only;
   * the prefetch path never runs inside quick-add/product-card contexts).
   * @param {string[]} optionValueIds
   * @returns {string}
   */
  #buildPrefetchUrl(optionValueIds) {
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${optionValueIds.join(',')}`);

    return `${this.dataset.productUrl}?${params.join('&')}`;
  }

  /**
   * Extracts the `option_values` query value from a request URL, used as the prefetch cache key so
   * lookups match regardless of URL form (absolute vs path, param order).
   * @param {string} requestUrl
   * @returns {string}
   */
  #optionValuesKeyFromUrl(requestUrl) {
    try {
      return new URL(requestUrl, window.location.origin).searchParams.get('option_values') || '';
    } catch {
      return '';
    }
  }

  /**
   * Preloads the hero gallery image from a fetched section response by mirroring its `<img>`
   * srcset/sizes onto a detached Image, so the browser fetches the exact candidate the gallery
   * will later display (matching viewport/DPR) and it's already decoded when the shape is selected.
   * @param {Document} html
   */
  #warmGalleryImage(html) {
    const img = html.querySelector('media-gallery img.product-media__image');
    if (!(img instanceof HTMLImageElement)) return;

    const warm = new Image();
    const sizes = img.getAttribute('sizes');
    const srcset = img.getAttribute('srcset');
    if (sizes) warm.sizes = sizes;
    if (srcset) warm.srcset = srcset;
    const src = img.getAttribute('src');
    if (src) warm.src = src;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option value IDs, limited to fieldsets up to and including a given index.
   * Values from later fieldsets are dropped, so the server can pick a default for them instead
   * of resolving a combination that may no longer be valid.
   * @param {number} maxFieldsetIndex
   * @returns {string[]}
   */
  getSelectedOptionsValuesUpTo(maxFieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    /** @type {string[]} */
    const values = [];

    fieldsets.forEach((fieldset, index) => {
      if (index > maxFieldsetIndex) return;
      const checked = fieldset?.querySelector('input:checked');
      const optionValueId = checked instanceof HTMLInputElement ? checked.dataset.optionValueId : undefined;
      if (optionValueId) values.push(optionValueId);
    });

    return values;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
